import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Button, Snackbar, Alert, CircularProgress } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import FormPreview from '../components/form-builder/FormPreview';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { PictureAsPdf as PdfIcon } from '@mui/icons-material';

const FormPdfManager = () => {
  const { formId } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const appToken = useSelector((state) => state.appToken.apptoken);
  const userData = useSelector((state) => state.loginData.loginData);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    const fetchFormData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`https://app.hhoms.com/api/form/Libraries/${formId}`, {
          headers: {
            ApplicationToken: appToken,
            User: userData.id,
          },
        });

        if (response.data.formLibraries && response.data.formLibraries[0]) {
          const formContent = response.data.formLibraries[0].formContent;
          const parsedData = JSON.parse(formContent);
          setFormData({
            ...parsedData,
            createDate: response.data.formLibraries[0].createDate,
            formId: response.data.formLibraries[0].id
          });
        } else {
          throw new Error('Form bulunamadı');
        }
      } catch (error) {
        console.error('Form verisi yüklenirken hata:', error);
        setSnackbar({
          open: true,
          message: 'Form verisi yüklenirken hata oluştu',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    if (formId && appToken && userData?.id) {
      fetchFormData();
    }
  }, [formId, appToken, userData?.id]);

  const handleGeneratePDF = async () => {
    if (!formData) {
      setSnackbar({
        open: true,
        message: 'Form verileri bulunamadı',
        severity: 'error'
      });
      return;
    }

    setGenerating(true);
    try {
      const element = document.getElementById('form-preview-container');
      if (!element) {
        throw new Error('Form elementi bulunamadı');
      }

      // Canvas oluştur
      const canvas = await html2canvas(element, {
        scale: 2, // Daha yüksek kalite için
        useCORS: true,
        logging: false,
        allowTaint: true,
        backgroundColor: '#ffffff',
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight,
        onclone: (clonedDoc) => {
          const clonedElement = clonedDoc.getElementById('form-preview-container');
          if (clonedElement) {
            // Form görünümünü optimize et
            clonedElement.style.padding = '20mm';
            clonedElement.style.margin = '0';
            clonedElement.style.width = '210mm';
            clonedElement.style.minHeight = '297mm';
            clonedElement.style.backgroundColor = '#ffffff';
            
            // Form başlığını düzenle
            const titleElement = clonedElement.querySelector('.MuiTypography-h4');
            if (titleElement) {
              titleElement.style.color = '#1976d2';
              titleElement.style.fontSize = '24px';
              titleElement.style.marginBottom = '20px';
              titleElement.style.textAlign = 'center';
            }

            // Alan etiketlerini düzenle
            const labels = clonedElement.querySelectorAll('.MuiFormLabel-root');
            labels.forEach(label => {
              label.style.color = '#1976d2';
              label.style.fontWeight = '600';
              label.style.fontSize = '14px';
            });

            // Input alanlarını düzenle
            const inputs = clonedElement.querySelectorAll('.MuiInputBase-root');
            inputs.forEach(input => {
              input.style.marginBottom = '16px';
              input.style.width = '100%';
            });
          }
        }
      });

      // PDF oluştur
      const imgData = canvas.toDataURL('image/jpeg', 1.0);
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 0; // Kenar boşluklarını kaldırdık çünkü form zaten padding içeriyor

      // Görüntü boyutlarını hesapla
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Sayfaları oluştur
      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      // İlk sayfayı ekle
      pdf.addImage(imgData, 'JPEG', margin, margin, imgWidth, imgHeight, '', 'FAST');
      heightLeft -= pageHeight;

      // Gerekirse ek sayfalar ekle
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pageNumber++;
        pdf.addImage(imgData, 'JPEG', margin, position + margin, imgWidth, imgHeight, '', 'FAST');
        heightLeft -= pageHeight;
      }

      // Her sayfaya altbilgi ekle
      for (let i = 1; i <= pageNumber; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(128, 128, 128);
        
        // Altbilgi çizgisi
        pdf.setDrawColor(200, 200, 200);
        pdf.line(margin + 10, pageHeight - 15, pageWidth - margin - 10, pageHeight - 15);
        
        // Altbilgi metinleri
        const date = new Date().toLocaleDateString('tr-TR');
        const pageText = `Sayfa ${i} / ${pageNumber}`;
        
        pdf.text(formData.title || 'Form', margin + 10, pageHeight - 10);
        pdf.text(pageText, pageWidth / 2, pageHeight - 10, { align: 'center' });
        pdf.text(date, pageWidth - margin - 10, pageHeight - 10, { align: 'right' });
      }

      // PDF'i kaydet
      const fileName = `${formData.title || 'form'}_${new Date().toLocaleDateString('tr-TR').replace(/\./g, '-')}.pdf`;
      pdf.save(fileName);

      setSnackbar({
        open: true,
        message: 'PDF başarıyla oluşturuldu',
        severity: 'success'
      });
    } catch (error) {
      console.error('PDF oluşturulurken hata:', error);
      setSnackbar({
        open: true,
        message: 'PDF oluşturulurken hata oluştu',
        severity: 'error'
      });
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">
            Form PDF Yöneticisi
          </Typography>
          <Box>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{ mr: 2 }}
            >
              Geri Dön
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleGeneratePDF}
              disabled={!formData || generating}
              startIcon={generating ? <CircularProgress size={20} /> : <PdfIcon />}
            >
              {generating ? 'PDF Oluşturuluyor...' : 'PDF Oluştur'}
            </Button>
          </Box>
        </Box>

        {formData && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" color="text.secondary">
              Form Başlığı: {formData.title}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Oluşturulma Tarihi: {new Date(formData.createDate).toLocaleDateString('tr-TR')}
            </Typography>
          </Box>
        )}
      </Paper>

      {formData && (
        <Paper 
          id="form-preview-container" 
          sx={{ 
            bgcolor: 'background.paper', 
            borderRadius: 1,
            boxShadow: 3,
            p: 4,
            '& .MuiFormLabel-root': {
              color: '#1976d2',
              fontWeight: 600
            },
            '& .MuiInputBase-input': {
              color: '#000000'
            },
            '& .MuiFormControl-root': {
              mb: 2
            },
            '@media print': {
              boxShadow: 'none',
              margin: 0,
              padding: '20mm',
              width: '210mm'
            }
          }}
        >
          <FormPreview
            formId={formId}
            formTitle={formData.title}
            formDescription={formData.description}
            formFields={formData.fields || []}
            settings={formData.settings}
            showPdfButton={true}
          />
        </Paper>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FormPdfManager;
